// Main App Component with Chakra UI
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ChakraProvider, defaultSystem, Toast, Stack, Spinner } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { HelmetProvider } from 'react-helmet-async';

// Import components
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import Dashboard from './pages/Dashboard';
import ReviewsPage from './pages/ReviewsPage';
import ReviewDetailPage from './pages/ReviewDetailPage';
// SettingsPage removed - settings now shown as dialog from Navbar
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import CreateReviewPage from './pages/CreateReviewPage';
import AdminPage from './pages/AdminPage';
import StandardsLibraryPage from './pages/StandardsLibraryPage';
import LandingPage from './pages/LandingPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import { AuthProvider } from './hooks/useAuth';
import ChatwootWidget from './components/ChatwootWidget';
import CookieConsent from './components/CookieConsent';
import { Toaster, toaster } from './components/ui/toaster';

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});


function App() {
  return (
    <HelmetProvider>
    <ChakraProvider value={defaultSystem}>
      <Toaster toaster={toaster}>
        {(toast) => (
          <Toast.Root width={{ md: 'sm' }}>
            {toast.type === 'loading' ? (
              <Spinner size="sm" color="blue.solid" />
            ) : (
              <Toast.Indicator />
            )}
            <Stack gap="1" flex="1" maxWidth="100%">
              {toast.title && <Toast.Title>{toast.title}</Toast.Title>}
              {toast.description && (
                <Toast.Description>{toast.description}</Toast.Description>
              )}
            </Stack>
            {toast.action && (
              <Toast.ActionTrigger>{toast.action.label}</Toast.ActionTrigger>
            )}
            {toast.meta?.closable && <Toast.CloseTrigger />}
          </Toast.Root>
        )}
      </Toaster>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router>
            <ChatwootWidget />
            <CookieConsent />
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Navbar />
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/create-review" element={
                <ProtectedRoute>
                  <Navbar />
                  <CreateReviewPage />
                </ProtectedRoute>
              } />
              <Route path="/reviews" element={
                <ProtectedRoute>
                  <Navbar />
                  <ReviewsPage />
                </ProtectedRoute>
              } />
              <Route path="/reviews/:id" element={
                <ProtectedRoute>
                  <ReviewDetailPage />
                </ProtectedRoute>
              } />

              <Route path="/standards" element={
                <ProtectedRoute>
                  <Navbar />
                  <StandardsLibraryPage />
                </ProtectedRoute>
              } />

              <Route path="/admin" element={
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              } />
            </Routes>
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </ChakraProvider>
    </HelmetProvider>
  );
}

export default App;